import { AppConfigContext } from '@config/AppConfig';
import { EModificationRequestPermission, ERolePermission } from '@userProfile/permission/permission.constants';
import {
  hasAllPermissionsInArray,
  hasAtLeastOnePermissionsInArray,
  isBelongToTeam,
  isRestrictedAccess,
  isPermittedAsSiteOperationUser,
  isPermittedAsSystemUser,
  hasPermissionInArray,
} from '@userProfile/permission/permission.services';
import React, { useContext, useMemo } from 'react';
import { EProjectTypeName } from '@project/project.constants';
import { ERiskCategory } from '@riskCategory/riskCategory.constants';
import {
  useHasPermission,
  useHasPermissions,
  useIsFromCompany,
  useIsFromCompanyByName,
  useIsFromCompanyByOrganizationTypeCodeByCompanyTypeName,
  useIsFromTeam,
  useProjectId,
  useProjectIsArchived,
} from '@config/config.hooks';
import { IMasterScheduleDetail } from '@masterSchedule/detail/masterScheduleDetail.models';
import {
  ECompanyCodeConst,
  ECompanyTypeIdConst,
  ECompanyTypeNameConst,
} from '@company/companyType/companyType.constants';
import { EProjectFileType } from '@file/projectFile/projectFile.constants';
import { useFetchAttachmentDesignModification } from '@file/projectFile/projectFile.queries';
import {
  useBelongToCompaniesInMasterSchedule,
  useNominatedInMasterSchedule,
} from '@masterSchedule/masterSchedule.hooks';
import { IRolePermissionFlattened } from '@userProfile/permission/permission.models';
import { EOrganizationTypeCode, EOrganizationTypeId } from '@company/organizationType/organizationType.constants';
import {
  EDesignModificationApprovalStatus,
  EDesignModificationIssueRole,
  EDesignModificationStatus,
  IProjectDesignModification,
} from './designModification.models';

export const useAllowNewT3 = () => {
  const appConfigs = useContext(AppConfigContext);

  if (appConfigs.configs.project?.type?.name === EProjectTypeName.TENDER) return false;

  // if (targetT1CompanyId !== userCompanyId) return false;

  // // TODO check master schedule type
  // if (masterScheduleIsCancelled || masterScheduleType === 'D') return false;

  const rolePermission =
    hasAtLeastOnePermissionsInArray(
      appConfigs.configs.myPermissions,
      [
        ERolePermission.ALL_DESIGNERS, // to-do: check if external company also can
        ERolePermission.DESIGNER_ADMINISTRATOR, // to-do: check if external company also can
      ],
      ECompanyTypeIdConst.DESIGNER,
    ) ||
    isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [
      ERolePermission.SITE_ENGINEER,
      ERolePermission.TEMPORARY_WORK_COORDINATOR,
      ERolePermission.TEMPORARY_WORK_COORDINATOR_FOR_MINOR_WORKS,
      ERolePermission.PROJECT_MANAGER,
      ERolePermission.ENGINEERING_MANAGER,
    ]);
  const initPermission = isPermittedT3Init(appConfigs.configs.myPermissions);
  return initPermission && rolePermission;
};

export const useAllowEditNominatedDesigner = (designModification?: IProjectDesignModification) => {
  const appConfigs = useContext(AppConfigContext);
  const isAllowed = isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [
    ERolePermission.SITE_ENGINEER,
    ERolePermission.TEMPORARY_WORK_COORDINATOR,
    ERolePermission.TEMPORARY_WORK_COORDINATOR_FOR_MINOR_WORKS,
    ERolePermission.PROJECT_MANAGER,
    ERolePermission.ENGINEERING_MANAGER,
  ]);

  return (
    isAllowed &&
    (!designModification ||
      (designModification.status !== EDesignModificationStatus.initial &&
        designModification.status !== EDesignModificationStatus.waitingToTWCApproval))
  );
};

export const useT3DetailGeneralFieldsEnabled = (
  isSuperseded: boolean,
  designModification?: IProjectDesignModification,
  masterSchedule?: IMasterScheduleDetail,
  issueRole?: EDesignModificationIssueRole,
) => {
  const appConfigs = useContext(AppConfigContext);
  const isShowIssueToTwcAsSrpBtn = useIsShowIssueToTwcAsSrpBtn(
    isSuperseded,
    designModification,
    masterSchedule,
    issueRole,
  );
  const isShowIssueToTwcAsDesignerBtn = useIsShowIssueToTwcAsDesignerBtn(
    isSuperseded,
    designModification,
    masterSchedule,
    issueRole,
  );
  const isShowIssueToDesignerBtn = useIsShowIssueToDesignerBtn(
    isSuperseded,
    designModification,
    masterSchedule,
    issueRole,
  );

  if (isSuperseded) return false;
  switch (designModification?.status) {
    case undefined:
    case null:
    case '':
    case EDesignModificationStatus.initial:
      return isShowIssueToTwcAsSrpBtn || isShowIssueToTwcAsDesignerBtn || isShowIssueToDesignerBtn;
    case EDesignModificationStatus.waitingToTWCApproval:
      return (
        isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [
          ERolePermission.TEMPORARY_WORK_COORDINATOR,
        ]) ?? false
      );
    case EDesignModificationStatus.waitingToDesignerApproval:
      return (
        hasPermissionInArray(
          appConfigs.configs.myPermissions,
          EModificationRequestPermission.DESIGNER_APPROVE,
          masterSchedule?.responsibleDesigner?.companyTypeId,
          masterSchedule?.responsibleDesigner?.organizationTypeId,
        ) ||
        isPermittedAsSystemUser(appConfigs.configs.myPermissions, [EModificationRequestPermission.DESIGNER_APPROVE])
      );
    default:
      return false;
  }
};

export const useT3DetailIsReadOnly = (
  isSuperseded: boolean,
  designModification?: IProjectDesignModification,
  masterSchedule?: IMasterScheduleDetail,
) => {
  const isProjectArchived = useProjectIsArchived();

  if (isSuperseded || isProjectArchived || masterSchedule?.isLocked || masterSchedule?.isCancelled) return true;
  switch (designModification?.status) {
    case EDesignModificationStatus.completed:
    case EDesignModificationStatus.cancel:
    case EDesignModificationStatus.revised:
    case EDesignModificationStatus.referToDesignerTransmittal:
      return true;
    default:
      return false;
  }
};

export interface IT3TopBtnsVisibility {
  isShowDesignerApproveBtn: boolean;
  isShowReviewerApproveBtn: boolean;
  isShowReassignICEBtn: boolean;
  isShowIceApproveBtn: boolean;
  isShowDesignAgreedAndCheckedByLambethBtn: boolean;
  isShowIssueToTwcAsSrpBtn: boolean;
  isShowIssueToTwcAsDesignerBtn: boolean;
  isShowIssueToDesignerBtn: boolean;
  isShowCancelBtn: boolean;
  isShowRequestEndorsementBtn: boolean;
  isShowSaveBtn: boolean;
  isShowIssueToCheckerBtn: boolean;
  isShowReleaseForConstructionBtn: boolean;
  isShowCreateRevisionBtn: boolean;
}

export const useT3TopBtnsVisibility: (
  id: number,
  isSuperseded: boolean,
  designModification?: IProjectDesignModification,
  issueRole?: EDesignModificationIssueRole,
  riskCategory?: ERiskCategory,
  masterSchedule?: IMasterScheduleDetail,
) => IT3TopBtnsVisibility = (id, isSuperseded, designModification, issueRole, riskCategory, masterSchedule) => {
  const projectId = useProjectId();

  const isShowSaveBtn = useIsShowSaveBtn(isSuperseded, designModification, masterSchedule, issueRole);
  const isShowIssueToTwcAsSrpBtn = useIsShowIssueToTwcAsSrpBtn(
    isSuperseded,
    designModification,
    masterSchedule,
    issueRole,
  );
  const isShowIssueToTwcAsDesignerBtn = useIsShowIssueToTwcAsDesignerBtn(
    isSuperseded,
    designModification,
    masterSchedule,
    issueRole,
  );
  const isShowIssueToDesignerBtn = useIsShowIssueToDesignerBtn(
    isSuperseded,
    designModification,
    masterSchedule,
    issueRole,
  );
  const isShowDesignerApproveBtn = useIsShowDesignerApproveBtn(isSuperseded, designModification, masterSchedule);
  const isShowIceApproveBtn = useIsShowIceApproveBtn(isSuperseded, designModification, masterSchedule);
  const isShowReviewerApproveBtn = useIsShowReviewerApproveBtn(isSuperseded, designModification, masterSchedule);
  const isShowDesignAgreedAndCheckedByLambethBtn = useIsShowDesignAgreedAndCheckedByLambethBtn(
    designModification,
    masterSchedule,
  );
  const isShowReassignICEBtn = useIsShowReassignIceBtn(isSuperseded, designModification, masterSchedule);
  const isShowIssueToCheckerBtn =
    useIsShowIssueToCheckerBtn(isSuperseded, designModification, masterSchedule) && !isShowReassignICEBtn;

  const isShowReleaseForConstructionBtn = useIsShowReleaseForConstructionBtn(
    isSuperseded,
    id,
    projectId,
    designModification,
    riskCategory,
    masterSchedule,
  );
  const isShowCancelBtn = useIsShowCancelBtn(isSuperseded, designModification, masterSchedule);
  const isShowRequestEndorsementBtn = useIsShowRequestEndorsementBtn(isSuperseded, designModification, masterSchedule);
  const isShowCreateRevisionBtn = useIsShowCreateRevisionBtn(isSuperseded, designModification, masterSchedule);

  return {
    isShowDesignerApproveBtn,
    isShowReviewerApproveBtn,
    isShowReassignICEBtn,
    isShowIceApproveBtn,
    isShowDesignAgreedAndCheckedByLambethBtn,
    isShowIssueToTwcAsSrpBtn,
    isShowIssueToTwcAsDesignerBtn,
    isShowIssueToDesignerBtn,
    isShowCancelBtn,
    isShowRequestEndorsementBtn,
    isShowSaveBtn,
    isShowIssueToCheckerBtn,
    isShowReleaseForConstructionBtn,
    isShowCreateRevisionBtn,
  };
};

export const isDesignModificationStatusInitial = (status: string | null | undefined) => {
  return status === null || status === undefined || status === '' || status === EDesignModificationStatus.initial;
};

const useIsShowIssueToTwcAsSrpBtn = (
  isSuperseded: boolean,
  designModification?: IProjectDesignModification,
  masterSchedule?: IMasterScheduleDetail,
  issueRole?: EDesignModificationIssueRole,
) => {
  const appConfigs = useContext(AppConfigContext);
  const isProjectArchived = useProjectIsArchived();
  const { isSrp } = useNominatedInMasterSchedule(masterSchedule);
  if (isSuperseded || isProjectArchived || masterSchedule?.isLocked || masterSchedule?.isCancelled) return false;
  if (!isDesignModificationStatusInitial(designModification?.status)) return false;
  if (!isSrp) return false;
  if (!issueRole || issueRole === EDesignModificationIssueRole.srp) {
    const isIssueToTwc =
      isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [ERolePermission.SITE_ENGINEER]) &&
      isPermittedT3Init(appConfigs.configs.myPermissions);
    if (isIssueToTwc) return true;
  }
  return false;
};

const useIsShowIssueToTwcAsDesignerBtn = (
  isSuperseded: boolean,
  designModification?: IProjectDesignModification,
  masterSchedule?: IMasterScheduleDetail,
  issueRole?: EDesignModificationIssueRole,
) => {
  const appConfigs = useContext(AppConfigContext);
  const { isNominatedDesigner } = useNominatedInMasterSchedule(masterSchedule);
  const isProjectArchived = useProjectIsArchived();

  if (isSuperseded || isProjectArchived || masterSchedule?.isLocked || masterSchedule?.isCancelled) return false;
  if (!isNominatedDesigner) return false;
  if (!isDesignModificationStatusInitial(designModification?.status)) return false;

  if (!issueRole || issueRole === EDesignModificationIssueRole.designer) {
    const isFormReading =
      isPermittedT3Init(appConfigs.configs.myPermissions) &&
      hasAtLeastOnePermissionsInArray(
        appConfigs.configs.myPermissions,
        [ERolePermission.ALL_DESIGNERS, ERolePermission.DESIGNER_ADMINISTRATOR],
        masterSchedule?.responsibleDesigner?.companyTypeId,
        masterSchedule?.responsibleDesigner?.organizationTypeId,
      );

    if (isFormReading) return true;
  }
  return false;
};

const useIsShowIssueToDesignerBtn = (
  isSuperseded: boolean,
  designModification?: IProjectDesignModification,
  masterSchedule?: IMasterScheduleDetail,
  issueRole?: EDesignModificationIssueRole,
) => {
  const appConfigs = useContext(AppConfigContext);
  const isProjectArchived = useProjectIsArchived();

  const { isTwc } = useNominatedInMasterSchedule(masterSchedule);

  if (isSuperseded || isProjectArchived || masterSchedule?.isLocked || masterSchedule?.isCancelled) return false;
  if (!isTwc) return false;

  if (isDesignModificationStatusInitial(designModification?.status)) {
    if (issueRole !== undefined && issueRole !== null && issueRole !== EDesignModificationIssueRole.twc) return false;
    return (
      isPermittedT3Init(appConfigs.configs.myPermissions) &&
      isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [ERolePermission.TEMPORARY_WORK_COORDINATOR])
    );
  }
  if (designModification?.status === EDesignModificationStatus.waitingToTWCApproval) {
    return (
      isPermittedT3Init(appConfigs.configs.myPermissions) &&
      isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [ERolePermission.TEMPORARY_WORK_COORDINATOR])
    );
  }
  return false;
};

const useIsShowCancelBtn = (
  isSuperseded: boolean,
  designModification?: IProjectDesignModification,
  masterSchedule?: IMasterScheduleDetail,
) => {
  const appConfigs = useContext(AppConfigContext);
  const isProjectArchived = useProjectIsArchived();

  const { isTwc } = useNominatedInMasterSchedule(masterSchedule);
  if (
    isSuperseded ||
    isProjectArchived ||
    masterSchedule?.isLocked ||
    masterSchedule?.isCancelled ||
    !designModification ||
    designModification?.status === EDesignModificationStatus.completed ||
    designModification?.status === EDesignModificationStatus.cancel ||
    designModification?.status === EDesignModificationStatus.revised ||
    designModification?.status === EDesignModificationStatus.referToDesignerTransmittal
  ) {
    return false;
  }
  return (
    isTwc &&
    isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [ERolePermission.TEMPORARY_WORK_COORDINATOR])
  );
};

const useIsShowSaveBtn = (
  isSuperseded: boolean,
  designModification?: IProjectDesignModification,
  masterSchedule?: IMasterScheduleDetail,
  issueRole?: EDesignModificationIssueRole,
) => {
  const appConfigs = useContext(AppConfigContext);
  const isProjectArchived = useProjectIsArchived();

  const { isTwc, isNominatedDesigner, isNominatedChecker } = useNominatedInMasterSchedule(masterSchedule);

  const isShowIssueToTwcAsSrpBtn = useIsShowIssueToTwcAsSrpBtn(
    isSuperseded,
    designModification,
    masterSchedule,
    issueRole,
  );
  const isShowIssueToTwcAsDesignerBtn = useIsShowIssueToTwcAsDesignerBtn(
    isSuperseded,
    designModification,
    masterSchedule,
    issueRole,
  );
  const isShowIssueToDesignerBtn = useIsShowIssueToDesignerBtn(
    isSuperseded,
    designModification,
    masterSchedule,
    issueRole,
  );
  const isT3NominatedDesigner = useIsFromTeam(designModification?.nominatedDesigner?.id ?? []);

  if (
    isSuperseded ||
    isProjectArchived ||
    masterSchedule?.isLocked ||
    masterSchedule?.isCancelled ||
    designModification?.status === EDesignModificationStatus.completed ||
    designModification?.status === EDesignModificationStatus.cancel ||
    designModification?.status === EDesignModificationStatus.waitingTWCToRelease ||
    designModification?.status === EDesignModificationStatus.revised ||
    designModification?.status === EDesignModificationStatus.referToDesignerTransmittal
  ) {
    return false;
  }
  if (designModification?.status === EDesignModificationStatus.waitingToUploadSignedCert) {
    return (
      isNominatedChecker &&
      hasPermissionInArray(
        appConfigs.configs.myPermissions,
        EModificationRequestPermission.ICE_APPROVE,
        masterSchedule?.responsibleChecker.companyTypeId,
        masterSchedule?.responsibleChecker.organizationTypeId,
      )
    );
  }
  if (
    isDesignModificationStatusInitial(designModification?.status) &&
    (isShowIssueToTwcAsSrpBtn || isShowIssueToTwcAsDesignerBtn || isShowIssueToDesignerBtn)
  )
    return true;

  if (designModification?.status === EDesignModificationStatus.waitingToTWCApproval) {
    return (
      isTwc &&
      isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions ?? [], [
        ERolePermission.TEMPORARY_WORK_COORDINATOR,
      ])
    );
  }
  if (designModification?.status === EDesignModificationStatus.waitingToDesignerApproval) {
    return (
      (isT3NominatedDesigner &&
        hasAtLeastOnePermissionsInArray(
          appConfigs.configs.myPermissions,
          [EModificationRequestPermission.DESIGNER_APPROVE],
          designModification.nominatedDesignerCompany?.companyTypeId,
          designModification.nominatedDesignerCompany?.organizationTypeId,
        )) ||
      (isNominatedDesigner &&
        hasAtLeastOnePermissionsInArray(
          appConfigs.configs.myPermissions,
          [EModificationRequestPermission.DESIGNER_APPROVE],
          masterSchedule?.responsibleDesigner?.companyTypeId,
          masterSchedule?.responsibleDesigner?.organizationTypeId,
        )) ||
      (isTwc &&
        isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions ?? [], [
          ERolePermission.TEMPORARY_WORK_COORDINATOR,
        ]))
    );
  }
  return (
    isTwc &&
    isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions ?? [], [ERolePermission.TEMPORARY_WORK_COORDINATOR])
  );
};

const useIsShowRequestEndorsementBtn = (
  isSuperseded: boolean,
  designModification?: IProjectDesignModification,
  masterSchedule?: IMasterScheduleDetail,
) => {
  const appConfigs = useContext(AppConfigContext);
  const isProjectArchived = useProjectIsArchived();

  const { isNominatedDesigner } = useNominatedInMasterSchedule(masterSchedule);
  const isT3NominatedDesigner = useIsFromTeam(designModification?.nominatedDesigner?.id ?? []);

  if (isSuperseded || isProjectArchived || masterSchedule?.isLocked || masterSchedule?.isCancelled) return false;
  if (!isNominatedDesigner && !isT3NominatedDesigner) return false;

  if (!designModification || designModification.status !== EDesignModificationStatus.waitingToDesignerApproval)
    return false;

  // to-do: check this too
  //     (ModificationRequestMasterSchedule.NominatedDesigner == DesignBriefDesigner.DesignerCompanyType.SubContractor
  //     || ModificationRequestMasterSchedule.NominatedDesigner == DesignBriefDesigner.DesignerCompanyType.Specialist
  //     || ModificationRequestMasterSchedule.NominatedDesigner == DesignBriefDesigner.DesignerCompanyType.Other)
  const isAllDesigner =
    hasAtLeastOnePermissionsInArray(
      appConfigs.configs.myPermissions,
      [ERolePermission.ALL_DESIGNERS],
      masterSchedule?.responsibleDesigner?.companyTypeId ?? designModification.nominatedDesignerCompany.companyTypeId,
      masterSchedule?.responsibleDesigner?.organizationTypeId ??
        designModification.nominatedDesignerCompany.organizationTypeId,
    ) ?? false;
  if (isAllDesigner) return true;

  const isDesignerApprove = hasAllPermissionsInArray(
    appConfigs.configs.myPermissions,
    [EModificationRequestPermission.DESIGNER_APPROVE],
    masterSchedule?.responsibleDesigner?.companyTypeId ?? designModification.nominatedDesignerCompany.companyTypeId,
    masterSchedule?.responsibleDesigner?.organizationTypeId ??
      designModification.nominatedDesignerCompany.organizationTypeId,
  );

  if (!isDesignerApprove) return false;

  return !hasAllPermissionsInArray(
    appConfigs.configs.myPermissions,
    [ERolePermission.DESIGNER_AUTHORISED_REPRESENTATIVE],
    masterSchedule?.responsibleDesigner?.companyTypeId ?? designModification.nominatedDesignerCompany.companyTypeId,
    masterSchedule?.responsibleDesigner?.organizationTypeId ??
      designModification.nominatedDesignerCompany.organizationTypeId,
  );
};

const useIsShowDesignerApproveBtn = (
  isSuperseded: boolean,
  designModification?: IProjectDesignModification,
  masterSchedule?: IMasterScheduleDetail,
) => {
  const appConfigs = useContext(AppConfigContext);
  const isProjectArchived = useProjectIsArchived();

  const { isNominatedDesigner } = useNominatedInMasterSchedule(masterSchedule);
  const isT3NominatedDesigner = useIsFromTeam(designModification?.nominatedDesigner?.id ?? []);

  const isLambethDesigner = useIsFromCompanyByOrganizationTypeCodeByCompanyTypeName(
    EOrganizationTypeCode.LAL,
    ECompanyTypeNameConst.DESIGNER,
  );
  if (isSuperseded || isProjectArchived || masterSchedule?.isLocked || masterSchedule?.isCancelled) return false;
  if (!isNominatedDesigner && !isLambethDesigner && !isT3NominatedDesigner) return false;
  if (designModification?.status !== EDesignModificationStatus.waitingToDesignerApproval) return false;
  const hasT1NominatedDesignerPermission = hasAllPermissionsInArray(
    appConfigs.configs.myPermissions,
    [ERolePermission.DESIGNER_AUTHORISED_REPRESENTATIVE, EModificationRequestPermission.DESIGNER_APPROVE],
    masterSchedule?.responsibleDesigner?.companyTypeId,
    masterSchedule?.responsibleDesigner?.organizationTypeId,
  );
  const hasT3NominatedDesignerPermission = hasAllPermissionsInArray(
    appConfigs.configs.myPermissions,
    [ERolePermission.DESIGNER_AUTHORISED_REPRESENTATIVE, EModificationRequestPermission.DESIGNER_APPROVE],
    designModification.nominatedDesignerCompany.companyTypeId,
    designModification.nominatedDesignerCompany.organizationTypeId,
  );
  const hasLambethDesignerPermission = hasAllPermissionsInArray(
    appConfigs.configs.myPermissions,
    [ERolePermission.DESIGNER_AUTHORISED_REPRESENTATIVE, EModificationRequestPermission.DESIGNER_APPROVE],
    ECompanyTypeIdConst.DESIGNER,
    EOrganizationTypeId.LAL,
  );
  return hasT1NominatedDesignerPermission || hasLambethDesignerPermission || hasT3NominatedDesignerPermission;
};

const useIsShowDesignAgreedAndCheckedByLambethBtn = (
  designModification?: IProjectDesignModification,
  masterSchedule?: IMasterScheduleDetail,
) => {
  // Remove this feature unless user request AB#4040
  // return false;

  // feature enabled due to user request AB#4175 2025-02-05
  const appConfigs = useContext(AppConfigContext);
  const { isNominatedChecker, isNominatedDesigner } = useNominatedInMasterSchedule(masterSchedule);

  if (!isNominatedChecker || !isNominatedDesigner) return false;
  if (designModification?.status !== EDesignModificationStatus.waitingToDesignerApproval) return false;

  const isPermitted =
    hasPermissionInArray(
      appConfigs.configs.myPermissions,
      ERolePermission.DESIGNER_AUTHORISED_REPRESENTATIVE,
      masterSchedule?.responsibleDesigner?.companyTypeId,
      masterSchedule?.responsibleDesigner?.organizationTypeId,
    ) &&
    hasPermissionInArray(
      appConfigs.configs.myPermissions,
      EModificationRequestPermission.ICE_APPROVE,
      masterSchedule?.responsibleChecker.companyTypeId,
      masterSchedule?.responsibleChecker.organizationTypeId,
    );
  if (!isPermitted) return false;

  const isDesignerCompanyLambeth = masterSchedule?.responsibleDesigner?.code === ECompanyCodeConst.LAMBETH;
  const isCheckerCompanyLambeth = masterSchedule?.responsibleChecker.code === ECompanyCodeConst.LAMBETH_CHECKER;

  return isDesignerCompanyLambeth && isCheckerCompanyLambeth;
};

const useIsShowReviewerApproveBtn = (
  isSuperseded: boolean,
  designModification?: IProjectDesignModification,
  masterSchedule?: IMasterScheduleDetail,
) => {
  const isProjectArchived = useProjectIsArchived();

  const { isNominatedReviewer } = useNominatedInMasterSchedule(masterSchedule);
  const isPermitted = useHasPermissions(
    [EModificationRequestPermission.REV_APPROVE, ERolePermission.REVIEWER_AUTHORISED_REPRESENTATIVE],
    masterSchedule?.responsibleReviewer?.companyTypeId,
    masterSchedule?.responsibleReviewer?.organizationTypeId,
  );
  if (isSuperseded || isProjectArchived || masterSchedule?.isLocked || masterSchedule?.isCancelled) return false;
  if (designModification?.status !== EDesignModificationStatus.waitingForReviewerApproval) return false;
  return isPermitted && isNominatedReviewer;
};
const useIsShowIssueToCheckerBtn = (
  isSuperseded: boolean,
  designModification?: IProjectDesignModification,
  masterSchedule?: IMasterScheduleDetail,
) => {
  const appConfigs = useContext(AppConfigContext);
  const isProjectArchived = useProjectIsArchived();

  const { isTwc } = useNominatedInMasterSchedule(masterSchedule);
  if (isSuperseded || isProjectArchived || masterSchedule?.isLocked || masterSchedule?.isCancelled) return false;
  if (!isTwc) return false;
  if (designModification?.status !== EDesignModificationStatus.waitingToTWCIssue) return false;
  if (designModification.iceStatus === EDesignModificationApprovalStatus.approved) return false;
  if (designModification.designerStatus !== EDesignModificationApprovalStatus.approved) return false;
  return isPermittedT3IssueToIce(appConfigs.configs.myPermissions);
};

const useIsShowIceApproveBtn = (
  isSuperseded: boolean,
  designModification?: IProjectDesignModification,
  masterSchedule?: IMasterScheduleDetail,
) => {
  const appConfigs = useContext(AppConfigContext);
  const isProjectArchived = useProjectIsArchived();

  if (isSuperseded || isProjectArchived || masterSchedule?.isLocked || masterSchedule?.isCancelled) return false;

  const isT3NominatedChecker = designModification?.nominatedChecker.id
    ? isBelongToTeam(appConfigs.configs.myTeams ?? [], designModification?.nominatedChecker.id)
    : false;

  if (!isT3NominatedChecker) return false;

  if (
    designModification?.status === EDesignModificationStatus.waitingToICEApproval ||
    designModification?.status === EDesignModificationStatus.waitingToUploadSignedCert
  ) {
    return hasAllPermissionsInArray(
      appConfigs.configs.myPermissions,
      [EModificationRequestPermission.ICE_APPROVE, ERolePermission.ICE_AUTHORISED_REPRESENTATIVE],
      masterSchedule?.responsibleChecker.companyTypeId,
      masterSchedule?.responsibleChecker.organizationTypeId,
    );
  }

  return false;
};

const useIsShowReassignIceBtn = (
  isSuperseded: boolean,
  designModification?: IProjectDesignModification,
  masterSchedule?: IMasterScheduleDetail,
) => {
  const appConfigs = useContext(AppConfigContext);
  const isProjectArchived = useProjectIsArchived();

  const { isTwc } = useNominatedInMasterSchedule(masterSchedule);

  if (isSuperseded || isProjectArchived || masterSchedule?.isLocked || masterSchedule?.isCancelled) return false;
  if (!isTwc) return false;
  if (designModification?.status !== EDesignModificationStatus.waitingToUploadSignedCert) return false;

  const canChangeIce = isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [
    EModificationRequestPermission.ALLOW_CHANGE_ICE,
  ]);
  if (!canChangeIce) return false;

  if (
    designModification?.iceStatus !== EDesignModificationApprovalStatus.approved &&
    designModification?.designerStatus === EDesignModificationApprovalStatus.approved
  ) {
    return true;
  }

  return false;
};

export const useIsIceCertificateUploaded = (designModificationId: number, projectId: number) => {
  const { data: attachmentsCertificate } = useFetchAttachmentDesignModification({
    projectId,
    fileType: EProjectFileType.T3_ICE,
    designModificationId,
  });
  return (attachmentsCertificate?.length ?? 0) > 0;
};

const useIsShowReleaseForConstructionBtn = (
  isSuperseded: boolean,
  designModificationId: number,
  projectId: number,
  designModification?: IProjectDesignModification,
  riskCategory?: ERiskCategory,
  masterSchedule?: IMasterScheduleDetail,
) => {
  const appConfigs = useContext(AppConfigContext);
  const isProjectArchived = useProjectIsArchived();

  const isICECertificateUploaded = useIsIceCertificateUploaded(designModificationId, projectId);
  const { isTwc } = useNominatedInMasterSchedule(masterSchedule);

  if (isSuperseded || isProjectArchived || masterSchedule?.isLocked || masterSchedule?.isCancelled) return false;
  if (!isTwc) return false;

  const canReleaseForConstruction = isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [
    EModificationRequestPermission.RELEASE_FOR_CONSTRUCTION,
  ]);
  const isCategoryD = riskCategory === ERiskCategory.D;

  if (designModification?.status === EDesignModificationStatus.waitingToTWCIssue) {
    return (
      (isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [
        EModificationRequestPermission.RELEASE_FOR_CONSTRUCTION,
      ]) ||
        isPermittedT3IssueToIce(appConfigs.configs.myPermissions)) &&
      isCategoryD
    );
  }

  if (designModification?.status === EDesignModificationStatus.waitingToUploadSignedCert) {
    if (isICECertificateUploaded) return true;
    return isCategoryD && canReleaseForConstruction;
  }
  if (designModification?.status === EDesignModificationStatus.waitingTWCToRelease) {
    if (isICECertificateUploaded) return true;
    return canReleaseForConstruction && !designModification.iceApproval;
  }
  return false;
};
const useIsShowCreateRevisionBtn = (
  isSuperseded: boolean,
  designModification?: IProjectDesignModification,
  masterSchedule?: IMasterScheduleDetail,
) => {
  const appConfigs = useContext(AppConfigContext);
  const isProjectArchived = useProjectIsArchived();

  const { isTwc, isSrp, isNominatedDesigner } = useNominatedInMasterSchedule(masterSchedule);

  if (isSuperseded || isProjectArchived || masterSchedule?.isLocked || masterSchedule?.isCancelled) return false;
  if (
    designModification?.status === EDesignModificationStatus.waitingToTWCApproval ||
    designModification?.status === EDesignModificationStatus.waitingToDesignerApproval
  ) {
    return (
      (isSrp && isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [ERolePermission.SITE_ENGINEER])) ||
      (isTwc &&
        isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [ERolePermission.TEMPORARY_WORK_COORDINATOR]))
    );
  }
  if (
    designModification?.status === EDesignModificationStatus.waitingToUploadSignedCert ||
    designModification?.status === EDesignModificationStatus.waitingToTWCIssue
  ) {
    return (
      isNominatedDesigner &&
      hasAtLeastOnePermissionsInArray(
        appConfigs.configs.myPermissions,
        [ERolePermission.ALL_DESIGNERS],
        masterSchedule?.responsibleDesigner?.companyTypeId,
        masterSchedule?.responsibleDesigner?.organizationTypeId,
      )
    );
  }

  return false;
};

export const useIsShowReleasedConstructionInfo = (
  designModificationId: number,
  projectId: number,
  designModification?: IProjectDesignModification,
  riskCategory?: ERiskCategory,
) => {
  const appConfigs = useContext(AppConfigContext);

  const isICECertificateUploaded = useIsIceCertificateUploaded(designModificationId, projectId);

  if (
    designModification?.status !== EDesignModificationStatus.waitingTWCToRelease &&
    designModification?.status !== EDesignModificationStatus.waitingToUploadSignedCert
  )
    return false;
  if (!isICECertificateUploaded) return false;
  if (designModification.status === EDesignModificationStatus.waitingTWCToRelease) return true;

  if (designModification.status === EDesignModificationStatus.waitingToUploadSignedCert) {
    const isCategoryD = riskCategory === ERiskCategory.D;
    if (isCategoryD) return false;

    return isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [
      EModificationRequestPermission.UPLOAD_RELEASED_FOR_CONSTRUCTION,
    ]);
  }
  return false;
};

export const useIsAttachmentUploadLocked = (masterSchedule?: IMasterScheduleDetail) => {
  const isDesignerCompanyRestricted = isRestrictedAccess(masterSchedule?.responsibleDesigner?.id);
  if (!isDesignerCompanyRestricted) return false;

  const isCheckerCompanyRestricted = isRestrictedAccess(masterSchedule?.responsibleChecker.id);
  if (!isCheckerCompanyRestricted) return false;

  return true;
};

export const useIsDesignerAttachmentUploadEnabled = (
  isSuperseded: boolean,
  designModification?: IProjectDesignModification,
  masterSchedule?: IMasterScheduleDetail,
) => {
  const appConfigs = useContext(AppConfigContext);
  const isProjectArchived = useProjectIsArchived();

  if (isSuperseded || isProjectArchived || masterSchedule?.isLocked || masterSchedule?.isCancelled) return false;
  if (!designModification || designModification.status !== EDesignModificationStatus.waitingToDesignerApproval)
    return false;

  // to-do: check this too
  //     (ModificationRequestMasterSchedule.NominatedDesigner == DesignBriefDesigner.DesignerCompanyType.SubContractor ||
  //       ModificationRequestMasterSchedule.NominatedDesigner == DesignBriefDesigner.DesignerCompanyType.Specialist ||
  //       ModificationRequestMasterSchedule.NominatedDesigner == DesignBriefDesigner.DesignerCompanyType.Other)
  //       &&
  //       AccessRight.HaveAccess(AccessRight.TemporaryWorkModule.ModificationRequest.Designer.DESIGNER_APPROVE, Session, LambethCompanyID)
  // )

  return hasAtLeastOnePermissionsInArray(
    appConfigs.configs.myPermissions,
    [EModificationRequestPermission.DESIGNER_APPROVE],
    masterSchedule?.responsibleDesigner?.companyTypeId,
    masterSchedule?.responsibleDesigner?.organizationTypeId,
  );
};

export const useIsProposedModificationAttachmentUploadEnabled = (
  designModification?: IProjectDesignModification,
  masterSchedule?: IMasterScheduleDetail,
) => {
  const appConfigs = useContext(AppConfigContext);
  const isProjectArchived = useProjectIsArchived();

  if (isProjectArchived || masterSchedule?.isLocked || masterSchedule?.isCancelled) return false;

  if (isDesignModificationStatusInitial(designModification?.status)) {
    return isPermittedT3Init(appConfigs.configs.myPermissions);
  }
  if (designModification?.status === EDesignModificationStatus.waitingToTWCApproval) {
    return (
      isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [
        EModificationRequestPermission.ISSUE_TO_DESIGNER,
      ]) ||
      isPermittedAsSystemUser(appConfigs.configs.myPermissions, [EModificationRequestPermission.ISSUE_TO_DESIGNER]) ||
      isPermittedT3Init(appConfigs.configs.myPermissions)
    );
  }
  return false;
};

export const useIsRiskCategoryEnabled = (designModification?: IProjectDesignModification) => {
  const appConfigs = useContext(AppConfigContext);

  if (
    designModification?.status === EDesignModificationStatus.cancel ||
    designModification?.status === EDesignModificationStatus.completed ||
    designModification?.status === EDesignModificationStatus.waitingToUploadSignedCert ||
    designModification?.status === EDesignModificationStatus.referToDesignerTransmittal ||
    designModification?.status === EDesignModificationStatus.revised
  )
    return false;

  return isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [
    ERolePermission.ENGINEERING_MANAGER,
    ERolePermission.TEMPORARY_WORK_COORDINATOR,
  ]);
};

export const useIsNominatedCheckerEnabled = (designModification?: IProjectDesignModification) => {
  const appConfigs = useContext(AppConfigContext);
  if (designModification?.status !== EDesignModificationStatus.waitingToUploadSignedCert) return false;
  if (designModification.iceStatus === EDesignModificationApprovalStatus.approved) return false;

  return isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [
    EModificationRequestPermission.ALLOW_CHANGE_ICE,
  ]);
};

export const useIsIceRequired = (designModification?: IProjectDesignModification) => {
  if (
    !designModification?.iceStatus ||
    designModification?.iceStatus === EDesignModificationApprovalStatus.approved ||
    designModification?.iceStatus === EDesignModificationApprovalStatus.notRequired
  ) {
    return true;
  }
  return false;
};

export const useT3CommentPermission = (
  isSuperseded: boolean,
  masterSchedule?: IMasterScheduleDetail,
  designModification?: IProjectDesignModification,
) => {
  const appConfigs = useContext(AppConfigContext);

  const { isNominatedDesigner, isNominatedReviewer } = useNominatedInMasterSchedule(masterSchedule);

  const isNominatedChecker = useIsFromTeam(designModification?.nominatedChecker.id ?? -1);

  const isFromLambeth = useIsFromCompanyByName('Lambeth');
  const isFromDesignerCompany = useIsFromCompany(
    designModification?.masterSchedule?.nominatedDesignerProjectCompanyId ?? 0,
  );

  const hasDesignerCommentCreatePermission = useHasPermission(
    EModificationRequestPermission.DESIGNER_COMMENT,
    masterSchedule?.responsibleDesigner?.companyTypeId,
    masterSchedule?.responsibleDesigner?.organizationTypeId,
  );
  const hasDesignerCommentReplyPermission = useHasPermission(
    EModificationRequestPermission.DESIGNER_COMMENT_RESPONSE,
    ECompanyTypeIdConst.SITE_OPERATION,
    EOrganizationTypeId.SITE,
  );
  const hasIceCommentCreatePermission = useHasPermission(
    EModificationRequestPermission.ICE_COMMENT,
    masterSchedule?.responsibleChecker.companyTypeId,
    masterSchedule?.responsibleChecker.organizationTypeId,
  );
  const hasIceCommentReplyPermission =
    isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [
      EModificationRequestPermission.ICE_COMMENT_RESPONSE,
    ]) ||
    hasPermissionInArray(
      appConfigs.configs.myPermissions,
      EModificationRequestPermission.ICE_COMMENT_RESPONSE,
      masterSchedule?.responsibleDesigner?.companyTypeId,
      masterSchedule?.responsibleDesigner?.organizationTypeId,
    );

  const enableDesignerComment = useMemo(() => {
    return (
      !!designModification &&
      !isSuperseded &&
      (designModification.status === EDesignModificationStatus.waitingToDesignerApproval ||
        (designModification.originalDesignModificationId ?? 0) > 0)
    );
  }, [designModification, isSuperseded]);

  const hasDesignerCommentCreateRight = React.useMemo(() => {
    return enableDesignerComment && hasDesignerCommentCreatePermission && (isFromDesignerCompany || isFromLambeth);
  }, [enableDesignerComment, hasDesignerCommentCreatePermission, isFromLambeth, isFromDesignerCompany]);

  const hasDesignerCommentReplyRight = React.useMemo(() => {
    return hasDesignerCommentReplyPermission && enableDesignerComment;
  }, [hasDesignerCommentReplyPermission, enableDesignerComment]);

  const isDesignerCommentReadOnly = useMemo(() => {
    if (hasDesignerCommentCreateRight || hasDesignerCommentReplyRight) return false;
    return isNominatedDesigner || isNominatedChecker;
  }, [isNominatedDesigner, isNominatedChecker, hasDesignerCommentCreateRight, hasDesignerCommentReplyRight]);

  const enableIceComment = useMemo(() => {
    return (
      !!designModification &&
      !isSuperseded &&
      designModification.status === EDesignModificationStatus.waitingToUploadSignedCert
    );
  }, [designModification, isSuperseded]);

  const hasIceCommentCreateRight = React.useMemo(() => {
    return hasIceCommentCreatePermission && enableIceComment && isNominatedChecker;
  }, [hasIceCommentCreatePermission, enableIceComment]);

  const hasIceCommentReplyRight = React.useMemo(() => {
    return hasIceCommentReplyPermission && enableIceComment;
  }, [hasIceCommentReplyPermission, enableIceComment]);

  const isIceCommentReadOnly = useMemo(() => {
    if (hasIceCommentCreateRight || hasIceCommentReplyRight) return false;
    return isNominatedDesigner || isNominatedChecker;
  }, [isNominatedDesigner, isNominatedChecker, hasIceCommentCreateRight, hasIceCommentReplyRight]);

  const enableReviewerComment = useMemo(() => {
    return (
      !!designModification &&
      !isSuperseded &&
      designModification.status === EDesignModificationStatus.waitingForReviewerApproval
    );
  }, [designModification, isSuperseded]);

  const hasReviewerCommentCreatePermission = useHasPermission(
    EModificationRequestPermission.REV_COMMENT,
    masterSchedule?.responsibleReviewer?.companyTypeId,
    masterSchedule?.responsibleReviewer?.organizationTypeId,
  );
  const hasReviewerCommentReplyPermission =
    isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [
      EModificationRequestPermission.REV_COMMENT_RESPONSE,
    ]) ||
    hasPermissionInArray(
      appConfigs.configs.myPermissions,
      EModificationRequestPermission.REV_COMMENT_RESPONSE,
      masterSchedule?.responsibleDesigner?.companyTypeId,
      masterSchedule?.responsibleDesigner?.organizationTypeId,
    );

  const hasReviewerCommentCreateRight = React.useMemo(() => {
    return hasReviewerCommentCreatePermission && enableReviewerComment && isNominatedReviewer;
  }, [hasReviewerCommentCreatePermission, enableReviewerComment]);

  const hasReviewerCommentReplyRight = React.useMemo(() => {
    return hasReviewerCommentReplyPermission && enableReviewerComment;
  }, [hasReviewerCommentReplyPermission, enableReviewerComment]);

  const isReviewerCommentReadOnly = useMemo(() => {
    if (hasReviewerCommentCreateRight || hasReviewerCommentReplyRight) return false;
    return isNominatedReviewer;
  }, [hasReviewerCommentCreateRight, hasReviewerCommentReplyRight, isNominatedReviewer]);

  const { isBelongToDesignerCompany, isBelongToCheckerCompany, isBelongToReviewerCompany } =
    useBelongToCompaniesInMasterSchedule(masterSchedule);
  const canViewDesignerDraftComment = isBelongToDesignerCompany && hasDesignerCommentCreateRight;
  const canViewCheckerDraftComment = isBelongToCheckerCompany && hasIceCommentCreateRight;
  const canViewReviewerDraftComment = isBelongToReviewerCompany && hasReviewerCommentCreateRight;

  return {
    isDesignerCommentReadOnly,
    isIceCommentReadOnly,
    isReviewerCommentReadOnly,
    hasDesignerCommentCreateRight,
    hasDesignerCommentReplyRight,
    hasIceCommentCreateRight,
    hasIceCommentReplyRight,
    hasReviewerCommentCreateRight,
    hasReviewerCommentReplyRight,
    canViewDesignerDraftComment,
    canViewCheckerDraftComment,
    canViewReviewerDraftComment,
  };
};

const isPermittedT3Init = (permissions: IRolePermissionFlattened[] = []) => {
  const initPermission =
    hasAllPermissionsInArray(
      permissions,
      [EModificationRequestPermission.INITATE],
      ECompanyTypeIdConst.DESIGNER, // to-do: check if external company also can
    ) ||
    isPermittedAsSiteOperationUser(permissions, [EModificationRequestPermission.INITATE]) ||
    isPermittedAsSystemUser(permissions, [EModificationRequestPermission.INITATE]);

  return initPermission;
};

const isPermittedT3IssueToIce = (permissions: IRolePermissionFlattened[] = []) => {
  return (
    isPermittedAsSiteOperationUser(permissions, [EModificationRequestPermission.ISSUE_TO_ICE]) ||
    isPermittedAsSystemUser(permissions, [EModificationRequestPermission.ISSUE_TO_ICE])
  );
};
